import React, { useState, useEffect } from 'react';
import { setUpTOTP, verifyTOTPSetup, updateMFAPreference } from 'aws-amplify/auth';
import QRCode from 'qrcode';
import './MFASetup.css';

interface MFASetupProps {
  onComplete?: () => void;
}

const MFASetup: React.FC<MFASetupProps> = ({ onComplete }) => {
  const [qrCode, setQrCode] = useState<string>('');
  const [secretCode, setSecretCode] = useState<string>('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isSetup, setIsSetup] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setupMFA();
  }, []);

  const setupMFA = async () => {
    try {
      setLoading(true);
      // Get TOTP setup details
      const totpSetup = await setUpTOTP();
      const setupUri = totpSetup.getSetupUri('SmartStorage');
      
      // Generate QR code
      const qrCodeDataUrl = await QRCode.toDataURL(setupUri.href);
      setQrCode(qrCodeDataUrl);
      
      // Show secret code for manual entry
      setSecretCode(totpSetup.sharedSecret);
    } catch (err: any) {
      setError('Failed to setup MFA. Please try again.');
      console.error('MFA setup error:', err);
    } finally {
      setLoading(false);
    }
  };

  const verifyAndEnable = async () => {
    if (verificationCode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      // Verify the code user entered
      await verifyTOTPSetup({ code: verificationCode });
      
      // Enable TOTP as preferred MFA
      await updateMFAPreference({ totp: 'PREFERRED' });
      
      setIsSetup(true);
      
      // Call onComplete callback if provided
      if (onComplete) {
        setTimeout(() => onComplete(), 2000);
      }
    } catch (err: any) {
      setError('Invalid code. Please try again.');
      console.error('MFA verification error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setVerificationCode(value);
    setError('');
  };

  if (isSetup) {
    return (
      <div className="mfa-success">
        <div className="success-icon">✓</div>
        <h2>Two-Factor Authentication Enabled</h2>
        <p>Your account is now protected with authenticator app MFA.</p>
        <p className="success-note">You'll need to enter a code from your authenticator app each time you sign in.</p>
      </div>
    );
  }

  return (
    <div className="mfa-setup">
      <h2>Setup Two-Factor Authentication</h2>
      <p className="mfa-description">
        Add an extra layer of security to your account by enabling two-factor authentication.
      </p>
      
      {loading && !qrCode ? (
        <div className="loading-container">
          <span className="loading-spinner"></span>
          <p>Setting up MFA...</p>
        </div>
      ) : (
        <>
          <div className="setup-steps">
            <div className="step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Install an Authenticator App</h3>
                <p>Download one of these apps if you don't have one:</p>
                <ul className="app-list">
                  <li>Google Authenticator</li>
                  <li>Microsoft Authenticator</li>
                  <li>Authy</li>
                  <li>1Password</li>
                </ul>
              </div>
            </div>

            <div className="step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>Scan QR Code</h3>
                <p>Open your authenticator app and scan this QR code:</p>
                {qrCode && (
                  <div className="qr-code-container">
                    <img src={qrCode} alt="QR Code" className="qr-code" />
                  </div>
                )}
                
                <div className="manual-entry">
                  <p className="manual-entry-label">Or enter this code manually:</p>
                  <code className="secret-code">{secretCode}</code>
                </div>
              </div>
            </div>

            <div className="step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Verify Setup</h3>
                <p>Enter the 6-digit code from your authenticator app:</p>
                <div className="verification-input-container">
                  <input
                    type="text"
                    maxLength={6}
                    value={verificationCode}
                    onChange={handleCodeChange}
                    placeholder="000000"
                    className="verification-input"
                    autoFocus
                  />
                </div>
                
                {error && <div className="error-message">{error}</div>}
                
                <button 
                  onClick={verifyAndEnable}
                  className="btn btn-primary verify-button"
                  disabled={loading || verificationCode.length !== 6}
                >
                  {loading ? <span className="loading-spinner"></span> : 'Verify & Enable MFA'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MFASetup;
